import React, { ReactNode } from 'react';
import { noop } from 'lodash';
import { Moment } from 'moment';
import { CustomerType } from 'pages/Complaint/InsuredDetails/enum';
import { initialFormData } from './initialState';
import { AddressDataType } from 'hooks/useSearchAddress';
enum FormActionType {
  UpdateForm = 'update-form',
  Reset = 'reset',
}

export const titleList = ['Mr', 'Ms', 'Miss', 'Mrs', 'Dr', 'Prof'] as const;
export type Title = (typeof titleList)[number];

export const genderList = ['Male', 'Female', 'Non-binary', 'Prefer not to say'] as const;
export type Gender = (typeof genderList)[number];

type FormAction = { type: FormActionType.UpdateForm; payload: object } | { type: FormActionType.Reset };

export type DateMoment = Date | Moment | null;

export const assistanceList = [
  'Extension of due date for payment',
  'Paying a reduced lump sum',
  'Paying in instalments',
  'Postponing one or more instalments',
  'Other',
] as const;
export type Assistance = (typeof assistanceList)[number];

export const sourceOfApplicationList = [
  'Email',
  'Face to face',
  'Online form',
  'Phone',
  'Post',
  'Referral from AFCA',
  'Social media',
  'Web chat',
  'Other',
] as const;

export const employmentTypeList = ['Casual', 'Contractor', 'Full-time', 'Part-time', 'Self employed'] as const;
export type EmploymentType = (typeof employmentTypeList)[number];

export const breachTypeList = [
  'Data, Privacy, Security',
  'Dealings Outside of TMD',
  'Financial Services Law',
  'GI Code of Practice',
  'QBE Agreement',
] as const;
export type BreachType = (typeof breachTypeList)[number];

export const productTypeList = [
  'Accident & Health',
  'Business Pack',
  'Farm',
  'Home – Building & Contents',
  'Landlord',
  'Motor',
  'Motorcycle',
  'Other',
  'Residential Strata',
] as const;
export type productType = (typeof productTypeList)[number];

export const gicopAreaList = [
  'Buying insurance',
  'Cancelling a policy',
  'Claims investigation',
  'Complaints',
  'Distributor standards',
  'Financial hardship',
  'Making a claim',
  'Service supplier standards',
  'Vulnerable customer',
  'Your access to information',
] as const;

export type GicopArea = (typeof gicopAreaList)[number];

export const incomeTypeList = [
  'Centrelink',
  'Dividends',
  'Investment',
  'Interest',
  'Pension',
  'Rent',
  'Other',
] as const;
export type IncomeType = (typeof incomeTypeList)[number];

export const YesNoList = ['yes', 'no'] as const;
export type YesNo = (typeof YesNoList)[number];

export type StepMethod =
  | 'Hardship'
  | 'EmploymentEdit'
  | 'EmploymentPreview'
  | 'IncomeEdit'
  | 'IncomePreview'
  | 'Expense';

export type Category = {
  id: string;
  name: string;
  heading: string;
};

export type Privacy = {
  heading: string;
  categories: Omit<Category, 'heading'>[];
};

export type EmploymentData = {
  id: string;
  employmentType: string;
  employerName: string;
  occupation: string;
  contactPerson: string;
  contactPersonNumber: string;
  salary: string | null;
};

export type IncomeData = {
  id: string;
  incomeType: string;
  incomeDescription: string;
  incomeAmount: string | null;
};

export type DependentData = {
  id: string;
  dependentName: string;
  dependentAge: string;
};

export interface ConfirmDetailsFormFields {
  lodgement: string | undefined;
}

export interface ConsentFormFields {
  policyOrClaim: string | null;
  policyNumber: string | null;
  claimNumber: string | null;
  personalDetails: boolean;
  contactDetails: boolean;
  relationship: string;
  qbeBusiness: string;
  firstName: string | null;
  lastName: string | null;
  organisation: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  contactType: string | null;
  contactPreferedMethod: string | null;
  postalAddress: string | null;
  postalAddressObject?: AddressDataType | null;
  isPostalAddressValid: boolean;
  isManualAddressEnabled: boolean;
  streetAddress: string | null;
  suburb: string | null;
  country: string | null;
  state: string | null;
  postcode: string | null;
}

export interface ResidentialAddressFields {
  residentialAddress: string | null;
  residentialAddressObject?: AddressDataType | null;
  rStreetAddress: string | null;
  rSuburb: string | null;
  rCountry: string | null;
  rState: string | null;
  rPostcode: string | null;
  isResAddressValid: boolean;
  isManualAddressRes: boolean;
}
export interface PhysicalAddressFields {
  cPostalAddress: string | null;
  cPostalAddressObject?: AddressDataType | null;
  pStreetAddress: string | null;
  pSuburb: string | null;
  pCountry: string | null;
  pState: string | null;
  pPostcode: string | null;
  isManualAddressPhy: boolean;
  isPhyAddressValid: boolean;
}

export interface InsuredDetailsFormFields extends ResidentialAddressFields, PhysicalAddressFields {
  insuredIndOrBus: CustomerType | null;
  title: string | null;
  insuredFirstName: string | null;
  insuredLastName: string | null;
  dependents: DependentData[];
  customerTitle: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  contactPersonTitle: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  dob: DateMoment;
  gender: string | null;
  iContactPreferedMethod: string | null;
  cEmailAddress: string | null;

  addressType: string | null;
  haveOneValidNumber: boolean | null;
  mPhoneNumber: string | null;
  hPhoneNumber: string | null;
  oPhoneNumber: string | null;

  isVulnerableCustomer: string | null;
  vulnerabilityReason: string | null;
  isLanguageInterpreter: string | null;
  language: string | null;
  otherLanguage: string | null;
  customerDesc: string | null;
  businessName: string | null;
  isSmallBusiness: string | null;
  contactType: string | null;
}

export interface ComplaintDetailsFormFields {
  complaintDate: DateMoment;
  sourceOfComplaint: string | null;
  acknowledgement: string | undefined;
  acknowledgementDate: Date | null;
  productType: string | null;
  compDescription: string | null;
  compType: string | null;
  compCategory: string[];
  sensitiveCase: string | undefined;
  resolved: string | undefined;
  desiredOutcome: string | null;
  resolvedDate: Date | null;
  outcomeType: string | null;
  outcomeDesc: string | null;
  amountInDispute: string | null;
  amountCompResolved: string | null;
  isValidDate: boolean; // ??
  files?: File[];
}

export interface HardshipFormFields {
  dateOfApplication: Date | null;
  sourceOfApplication: string | null;
  reason: string | null;
  assistance: string | null;
  description: string | null;
}

export interface EmploymentFormFields {
  employmentStatus: string | null;
  employmentData: EmploymentData[];
}

export interface IncomeFormFields {
  incomeStatus: string | null;
  incomeData: IncomeData[];
}

export interface ExpenseFormFields {
  rentMortgage: number | undefined;
  childSupport: number | undefined;
  otherLoan: number | undefined;
  motorVehicle: number | undefined;
  creditCard: number | undefined;
  livingCosts: number | undefined;
  utilities: number | undefined;
  schoolFees: number | undefined;
  hospitalMedicalCosts: number | undefined;
  insurance: number | undefined;
  otherExpense: number | undefined;
  declaration: boolean;
  files?: File[];
}

export interface IncidentTypeFormFields {
  breachType: string | null;
  rootCause: string[];
}

export interface IncidentDetailsFormFields {
  occuredDate: DateMoment;
  discoveredDate: DateMoment;
  breachDescription: string;
  tmdDescription: string;
  gicopArea: string | null;
  categoryList: Category[];
  dataPrivacy: string[];
  otherDetails?: string;
  hasDataPrivacy: boolean;
}

export interface BreachOtherDetailsFormFields {
  productType: string | null;
  policyNumber: string | null;
  claimNumber: string | null;
  customersAffected: string | null; // Yes/No
  financialLoss: string | null;
  customerNumber: string | null;
  estimatedLoss?: string;
}

export interface BreachResolutionFormFields {
  resolved: string | null; // Yes or No
  resolvedDate: DateMoment;
  resolutionDesc: string | null;
}

export interface TellUsMoreFormFields
  extends HardshipFormFields,
    EmploymentFormFields,
    IncomeFormFields,
    ExpenseFormFields {
  // mode: StepMethod;
}

export type FormDataType = ConfirmDetailsFormFields &
  ConsentFormFields &
  InsuredDetailsFormFields &
  ComplaintDetailsFormFields &
  HardshipFormFields &
  EmploymentFormFields &
  IncomeFormFields &
  ExpenseFormFields &
  IncidentTypeFormFields &
  IncidentDetailsFormFields &
  BreachOtherDetailsFormFields &
  BreachResolutionFormFields;

interface FormState {
  formData: FormDataType;
}

interface FormProviderProps {
  children: ReactNode;
}

interface FormContextProps {
  state: FormState;
  dispatch: (action: FormAction) => void;
}

export const keyNames = Object.keys(initialFormData);

const initialState: FormState = {
  formData: initialFormData,
};

const FormContext = React.createContext<FormContextProps>({ state: initialState, dispatch: () => noop });

const formReducer: React.Reducer<FormState, FormAction> = (state, action) => {
  switch (action.type) {
    case FormActionType.UpdateForm:
      return { ...state, formData: { ...state.formData, ...action.payload } };
    case FormActionType.Reset:
      return initialState;
    default:
      throw new Error('Unhandled action type');
  }
};

const FormProvider = ({ children }: FormProviderProps) => {
  const [state, dispatch] = React.useReducer(formReducer, initialState);
  return <FormContext.Provider value={{ state, dispatch }}>{children}</FormContext.Provider>;
};

const useFormState = () => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error(`useForm must be used within a FormProvider`);
  }

  const {
    state: { formData },
    dispatch,
  } = context;

  const { lodgement } = formData || {};

  const updateForm = async (newData: Partial<FormDataType>) =>
    dispatch({ type: FormActionType.UpdateForm, payload: newData });

  const resetForm = () => dispatch({ type: FormActionType.Reset });

  const COMPLAINT = 'complaint';
  const BREACH = 'breach';
  const FINANCIAL_HARDSHIP = 'fha';

  const isFNH = lodgement === FINANCIAL_HARDSHIP;
  const isBreach = lodgement === BREACH;
  const isComplaint = lodgement === COMPLAINT;

  const [subjectPrefix, setSubjectPrefix] = React.useState<ConfirmDetailsFormFields['lodgement']>('');

  const initialErrorMessage = 'Something has gone wrong. We apologise for any inconvenience.';

  const genericErrorMessage = `${initialErrorMessage} Please try again later or give us a call on 1300 650 503.`;
  const breachErrorMessage = `${initialErrorMessage} Please try again later or contact your QBE representative.`;
  const fhaErrorMessage = `${initialErrorMessage} Please try again later or give us a call on 1300 902 417.`;

  const [defaultErrorMessage, setDefaultErrorMessage] = React.useState<string>(genericErrorMessage);

  React.useEffect(() => {
    let newLodgement = undefined;
    setDefaultErrorMessage(genericErrorMessage);
    switch (lodgement) {
      case COMPLAINT:
        newLodgement = 'CNT';
        break;
      case FINANCIAL_HARDSHIP:
        newLodgement = 'FNH';
        setDefaultErrorMessage(fhaErrorMessage);
        break;
      case BREACH:
        newLodgement = 'BRH';
        setDefaultErrorMessage(breachErrorMessage);
        break;
    }

    setSubjectPrefix(newLodgement);
  }, [breachErrorMessage, fhaErrorMessage, genericErrorMessage, lodgement]);

  return {
    defaultErrorMessage,
    formData,
    updateForm,
    resetForm,
    isFNH,
    isBreach,
    isComplaint,
    subjectPrefix,
  };
};

export { FormProvider, useFormState };
